import React, { useState, useRef } from 'react';
import { devLog, devWarn, devError } from '../../../utils/devLogger';
import GenericListUI, { FetchParams, FetchResult } from '../../../components/CustomList/GenericListUI';
import { ColumnDefinition } from '../../../components/CustomList/GenericDataTable';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

// dayjs 플러그인 확장
dayjs.extend(isBetween);

// 결제 데이터 타입 정의
interface PaymentData {
  id?: number;
  no: number;
  paymentDate: string; // 결제일시
  artworkTitle: string; // 작품명
  lotId: string; // LOT ID
  totalAmountKRW: number; // 총 결제 금액(KRW)
  totalAmountUSD: number; // 총 결제 금액(USD)
  paymentMethod: string; // 결제수단
  paymentStatus: string; // 결제상태
  winnerName: string; // 낙찰자
  winnerCountry: string; // 낙찰자 국가
  winnerEmail: string; // 낙찰자 이메일
  winnerPhone: string; // 낙찰자 연락처
  refundStatus: string; // 환불 처리상태
  handlerName: string; // 처리 담당자
  handlerId: string; // 담당자 ID
  memo: string; // 메모
}

const PaymentManagementPage: React.FC = () => {
  const [refreshTrigger] = useState(0);
  const listRef = useRef<{ refetch: () => void }>(null);
  const [totalPaymentAmount, setTotalPaymentAmount] = useState<string>('0 (VAT 포함)');

  // 컴포넌트 마운트 시 로그
  React.useEffect(() => {
    devLog('PaymentManagement 컴포넌트 마운트됨');
  }, []);

  // 결제상태 포맷팅 함수
  const formatPaymentStatus = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return '결제완료';
      case 'PENDING':
        return '결제대기';
      case 'FAILED':
        return '결제실패';
      case 'CANCELED':
        return '결제취소';
      default:
        return status;
    }
  };

  // 환불상태 포맷팅 함수
  const formatRefundStatus = (status: string) => {
    switch (status) {
      case 'NONE':
        return '-';
      case 'REQUESTED':
        return '환불요청';
      case 'PROCESSING':
        return '환불처리중';
      case 'COMPLETED':
        return '환불완료';
      case 'REJECTED':
        return '환불거절';
      default:
        return status;
    }
  };

  // 금액 포맷팅 함수
  const formatAmount = (amount: number, currency: string) => {
    return `${amount.toLocaleString()} ${currency}`;
  };

  // 컬럼 정의
  const columns: ColumnDefinition<PaymentData>[] = [
    {
      header: 'No',
      accessor: 'no',
      formatter: (value) => value?.toString() || '',
    },
    {
      header: '결제일시',
      accessor: 'paymentDate' as keyof PaymentData,
      formatter: (value) => {
        if (!value) return '';
        return dayjs(value).format('YYYY.MM.DD HH:mm');
      },
    },
    {
      header: '작품명',
      accessor: 'artworkTitle',
      formatter: (value) => value || '-',
    },
    {
      header: 'LOT ID',
      accessor: 'lotId',
      formatter: (value) => value || '-',
    },
    {
      header: '총 결제 금액(KRW)',
      accessor: 'totalAmountKRW' as keyof PaymentData,
      formatter: (value) => formatAmount(value as number, 'KRW'),
    },
    {
      header: '총 결제 금액(USD)',
      accessor: 'totalAmountUSD' as keyof PaymentData,
      formatter: (value) => formatAmount(value as number, 'USD'),
    },
    {
      header: '결제수단',
      accessor: 'paymentMethod',
      formatter: (value) => value || '-',
    },
    {
      header: '결제상태',
      accessor: 'paymentStatus' as keyof PaymentData,
      formatter: (value) => formatPaymentStatus(value as string),
    },
    {
      header: '낙찰자',
      accessor: 'winnerName',
      formatter: (value) => value || '-',
    },
    {
      header: '낙찰자 국가',
      accessor: 'winnerCountry',
      formatter: (value) => value || '-',
    },
    {
      header: '낙찰자 이메일',
      accessor: 'winnerEmail',
      formatter: (value) => value || '-',
    },
    {
      header: '낙찰자 연락처',
      accessor: 'winnerPhone',
      formatter: (value) => value || '-',
    },
    {
      header: '환불 처리상태',
      accessor: 'refundStatus' as keyof PaymentData,
      formatter: (value) => formatRefundStatus(value as string),
    },
    {
      header: '처리 담당자',
      accessor: 'handlerName',
      formatter: (value) => value || '-',
    },
    {
      header: '담당자 ID',
      accessor: 'handlerId',
      formatter: (value) => value || '-',
    },
    {
      header: '메모',
      accessor: 'memo',
      formatter: (value) => value || '-',
    },
  ];

  // 더미 데이터를 반환하는 fetch 함수
  const fetchPaymentData = async (params: FetchParams): Promise<FetchResult<PaymentData>> => {
    devLog('fetchPaymentData 시작:', params);
    
    // 더미 데이터 생성
    const dummyData: PaymentData[] = [
      {
        id: 1,
        no: 1,
        paymentDate: '2025-10-10T14:30:00Z',
        artworkTitle: '모네의 수련',
        lotId: 'LOT001',
        totalAmountKRW: 15000000,
        totalAmountUSD: 11250,
        paymentMethod: '신용카드',
        paymentStatus: 'COMPLETED',
        winnerName: '김철수',
        winnerCountry: '대한민국',
        winnerEmail: 'kim@example.com',
        winnerPhone: '010-1234-5678',
        refundStatus: 'NONE',
        handlerName: '이관리',
        handlerId: 'admin001',
        memo: '정상 결제 완료',
      },
      {
        id: 2,
        no: 2,
        paymentDate: '2025-10-12T09:15:00Z',
        artworkTitle: '피카소의 게르니카',
        lotId: 'LOT002',
        totalAmountKRW: 25000000,
        totalAmountUSD: 18750,
        paymentMethod: '계좌이체',
        paymentStatus: 'COMPLETED',
        winnerName: 'John Smith',
        winnerCountry: '미국',
        winnerEmail: 'john.smith@example.com',
        winnerPhone: '+1-555-0123',
        refundStatus: 'REQUESTED',
        handlerName: '박관리',
        handlerId: 'admin002',
        memo: '환불 요청 접수',
      },
      {
        id: 3,
        no: 3,
        paymentDate: '2025-10-13T16:45:00Z',
        artworkTitle: '반 고흐의 별이 빛나는 밤',
        lotId: 'LOT003',
        totalAmountKRW: 35000000,
        totalAmountUSD: 26250,
        paymentMethod: '가상계좌',
        paymentStatus: 'PENDING',
        winnerName: '최영희',
        winnerCountry: '대한민국',
        winnerEmail: 'choi@example.com',
        winnerPhone: '010-9876-5432',
        refundStatus: 'NONE',
        handlerName: '정관리',
        handlerId: 'admin003',
        memo: '결제 확인 중',
      },
    ];

    devLog('더미 데이터 생성 완료:', dummyData.length, '개');

    // 검색 필터링 (키워드가 있는 경우)
    let filteredData = [...dummyData];
    if (params.keyword) {
      devLog('키워드 필터링:', params.keyword);
      const keyword = params.keyword.toLowerCase();
      filteredData = dummyData.filter(item => 
        item.artworkTitle.toLowerCase().includes(keyword) ||
        item.lotId.toLowerCase().includes(keyword) ||
        item.winnerName.toLowerCase().includes(keyword) ||
        item.winnerEmail.toLowerCase().includes(keyword) ||
        item.handlerName.toLowerCase().includes(keyword)
      );
    }

    // 날짜 필터링 (날짜 범위가 있는 경우)
    if (params.fromDate && params.toDate) {
      devLog('날짜 필터링:', params.fromDate, '~', params.toDate);
      const fromDate = dayjs(params.fromDate);
      const toDate = dayjs(params.toDate);
      filteredData = filteredData.filter(item => {
        const paymentDate = dayjs(item.paymentDate);
        return paymentDate.isBetween(fromDate, toDate, 'day', '[]');
      });
    }

    // 각 아이템에 id가 없는 경우 생성
    const processedData = filteredData.map((item, index) => ({
      ...item,
      id: item.id || index + 1,
      no: item.no || index + 1,
    }));

    // 기간 내 총 결제액 (KRW) 계산
    const totalPaymentAmount = processedData.reduce((sum, item) => sum + item.totalAmountKRW, 0);
    const formattedTotalAmount = `${totalPaymentAmount.toLocaleString()} (VAT 포함)`;
    
    // 총액 상태 업데이트
    setTotalPaymentAmount(formattedTotalAmount);

    const result: FetchResult<PaymentData> = {
      data: processedData,
      totalItems: processedData.length,
      allItems: dummyData.length,
    };

    devLog('결제 데이터 결과:', result);
    return Promise.resolve(result);
  };

  // 행 클릭 핸들러 (향후 상세 팝업 구현 시 사용)
  const handleRowClick = (item: PaymentData, index: number) => {
    devLog('결제 정보 클릭:', item);
    // 향후 결제 상세 팝업이나 수정 기능 구현 시 여기에 로직 추가
  };

  return (
    <>
      <GenericListUI<PaymentData>
        ref={listRef}
        title="결제 관리"
        columns={columns}
        fetchData={fetchPaymentData}
        excelFileName="PaymentList"
        totalAmountLabel="기간 내 결제액"
        totalAmount={totalPaymentAmount}
        keyExtractor={(item, index) => {
          return item.id ? item.id.toString() : `payment-${index}`;
        }}
        enableSearch={true}
        searchPlaceholder="작품명, LOT ID, 낙찰자명, 이메일, 담당자명으로 검색"
        enableDateFilter={true}
        dateRangeOptions={['금월', '지난달', '1년', '지정']}
        itemsPerPageOptions={[10, 20, 50, 100]}
        themeMode="light"
        onRowClick={handleRowClick}
        refreshTrigger={refreshTrigger}
      />
    </>
  );
};

export default PaymentManagementPage;
